// Angular Files
import { Component, Inject } from '@angular/core';

// Angular Material Files
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

// Teller Online Files
import {
    ActiveAuthorizationTextDto,
    AuthorizationConsentDto,
    PaymentAuthorizationTypeEnumDto,
    PaymentMethodTypeEnumDto
} from 'apps/public-portal/src/app/core/api/PublicPortalApiClients';

export interface PaymentConsentDialogData {
    paymentMethodType: PaymentMethodTypeEnumDto;
    authorizationType: PaymentAuthorizationTypeEnumDto;
    // Active text the caller already fetched in its pre-check. Seeded into the
    // child consent component so it does not fetch again (single source of truth).
    activeText: ActiveAuthorizationTextDto;
}

/**
 * Shared consent modal opened in Modal Mode for ad-hoc checkout and
 * scheduled-payment creation. Unlike SavedMethodPaymentConsentComponent (which is
 * hard-coded to AdHoc and lives under features/checkout), this dialog accepts both
 * paymentMethodType and authorizationType so it can serve any consent flow.
 *
 * Wraps <app-authorization-consent> which self-fetches the active text and emits
 * the consent payload via (consentChange). The dialog collects that payload but
 * does NOT submit anything itself — it closes with the captured
 * AuthorizationConsentDto and the caller includes it in the payCart / schedule
 * request.
 *
 * On accept → resolves with AuthorizationConsentDto.
 * On cancel → resolves with null (caller should abort the submit).
 */
@Component({
    selector: 'app-payment-consent-dialog',
    templateUrl: './payment-consent-dialog.component.html',
    standalone: false
})
export class PaymentConsentDialogComponent {
    public consent: AuthorizationConsentDto | null = null;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: PaymentConsentDialogData,
        private dialogRef: MatDialogRef<
            PaymentConsentDialogComponent, AuthorizationConsentDto | null>
    ) {}

    public onConsentChange(consent: AuthorizationConsentDto | null): void {
        this.consent = consent;
    }

    public get canConfirm(): boolean {
        // Consent is resolved once the child component fetches the active text
        // and emits a non-null payload. Clicking "Authorize and Pay" is the
        // binding consent act.
        return this.consent !== null;
    }

    public onClick_confirm(): void {
        if (!this.canConfirm) return;
        this.dialogRef.close(this.consent);
    }

    public onClick_cancel(): void {
        this.dialogRef.close(null);
    }
}
